import { useState } from "react"
import { createVehicle } from "../../../../services/api"


const FormCreateVehicle = () => {
  const [formData, setFormData] = useState({
    vehicleName: "",
    vehicleNumber: "",
    typeOfVehical: "",
    ManagerName: "",
    address: "",
    mobileNumber: "",
    notes: "",
  })

  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [successMessage, setSuccessMessage] = useState("")

  // Vehicle type options
  const vehicleTypes = [
    "Car",
    "Truck",
    "Van",
    "Motorcycle",
    "Bus",
    "Trailer",
    "Crane",
    "Excavator",
    "Bulldozer",
    "Other"
  ]

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !loading) {
      handleSubmit()
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" })
    }
    
    // Clear success message when form is modified
    if (successMessage) {
      setSuccessMessage("")
    }
  }

  const validateForm = () => {
    const newErrors = {}

    // Required field validations
    if (!formData.vehicleName.trim()) {
      newErrors.vehicleName = "Vehicle name is required"
    }

    if (!formData.vehicleNumber.trim()) {
      newErrors.vehicleNumber = "Vehicle number is required"
    } else if (formData.vehicleNumber.length < 4) {
      newErrors.vehicleNumber = "Vehicle number must be at least 4 characters"
    }

    // Mobile number validation (if provided)
    if (formData.mobileNumber && !/^\d{10}$/.test(formData.mobileNumber)) {
      newErrors.mobileNumber = "Mobile number must be exactly 10 digits"
    }

    // Vehicle number format validation (basic)
    if (formData.vehicleNumber && !/^[A-Za-z0-9]+$/.test(formData.vehicleNumber)) {
      newErrors.vehicleNumber = "Vehicle number should only contain letters and numbers"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }

    setLoading(true)
    setSuccessMessage("")
    
    try {
      // Prepare data for submission
      const submitData = {
        vehicleName: formData.vehicleName.trim(),
        vehicleNumber: formData.vehicleNumber.trim().toUpperCase(),
        typeOfVehical: formData.typeOfVehical.trim(),
        ManagerName: formData.ManagerName.trim(),
        address: formData.address.trim(),
        mobileNumber: formData.mobileNumber.trim(),
        notes: formData.notes.trim(),
      }

      const response = await createVehicle(submitData)
      
      if (response.success) {
        setSuccessMessage("Vehicle created successfully!")
        
        // Reset form
        setFormData({
          vehicleName: "",
          vehicleNumber: "",
          typeOfVehical: "",
          ManagerName: "",
          address: "",
          mobileNumber: "",
          notes: "",
        })
        setErrors({})
        
        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage("")
        }, 5000)
      }
    } catch (error) {
      console.error("Error creating vehicle:", error)
      
      // Handle specific error cases
      if (error.message && error.message.includes("already exists")) {
        setErrors({ vehicleNumber: error.message })
      } else {
        setErrors({ general: error.message || "Failed to create vehicle. Please try again." })
      }
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setFormData({
      vehicleName: "",
      vehicleNumber: "",
      typeOfVehical: "",
      ManagerName: "",
      address: "",
      mobileNumber: "",
      notes: "",
    })
    setErrors({})
    setSuccessMessage("")
  }

  return (
    <div className="bg-white p-6 border border-gray-200 shadow-lg rounded-xl max-w-4xl mx-auto" onKeyDown={handleKeyDown}>
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-6 w-6 text-gray-700"
          >
            <path d="M14 16V8a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2Z"/>
            <path d="M15 2c-1.35 0-2.5.5-2.5 1.5S13.65 5 15 5s2.5-.5 2.5-1.5S16.35 2 15 2Z"/>
            <path d="M17 6.5c-1 0-1.5.5-1.5 1.5S16 9.5 17 9.5s1.5-.5 1.5-1.5S18 6.5 17 6.5Z"/>
          </svg>
          <h2 className="text-2xl font-semibold text-gray-800">Add New Vehicle</h2>
        </div>
        <p className="text-gray-600 mt-1">Register a new vehicle in the system</p>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
          <div className="flex">
            <div className="text-green-600">
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-2">
              <p className="text-sm font-medium text-green-800">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* General Error Message */}
      {errors.general && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex">
            <div className="text-red-600">
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-2">
              <p className="text-sm font-medium text-red-800">{errors.general}</p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Vehicle Name */}
          <div className="space-y-2">
            <label htmlFor="vehicleName" className="text-sm font-medium text-gray-700">
              Vehicle Name *
            </label>
            <input
              id="vehicleName"
              name="vehicleName"
              type="text"
              placeholder="Enter vehicle name (e.g., Honda Civic)"
              value={formData.vehicleName}
              onChange={handleChange}
              required
              className={`w-full p-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                errors.vehicleName ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.vehicleName && <p className="text-red-500 text-xs">{errors.vehicleName}</p>}
          </div>

          {/* Vehicle Number */}
          <div className="space-y-2">
            <label htmlFor="vehicleNumber" className="text-sm font-medium text-gray-700">
              Vehicle Number *
            </label>
            <input
              id="vehicleNumber"
              name="vehicleNumber"
              type="text"
              placeholder="Enter vehicle number (e.g., MH12AB1234)"
              value={formData.vehicleNumber}
              onChange={handleChange}
              required
              className={`w-full p-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 uppercase ${
                errors.vehicleNumber ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.vehicleNumber && <p className="text-red-500 text-xs">{errors.vehicleNumber}</p>}
          </div>

          {/* Vehicle Type */}
          <div className="space-y-2">
            <label htmlFor="typeOfVehical" className="text-sm font-medium text-gray-700">
              Type of Vehicle
            </label>
            <select
              id="typeOfVehical"
              name="typeOfVehical"
              value={formData.typeOfVehical}
              onChange={handleChange}
              className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="">Select Vehicle Type</option>
              {vehicleTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {/* Manager Name */}
          <div className="space-y-2">
            <label htmlFor="ManagerName" className="text-sm font-medium text-gray-700">
              Manager Name
            </label>
            <input
              id="ManagerName"
              name="ManagerName"
              type="text"
              placeholder="Enter manager name"
              value={formData.ManagerName}
              onChange={handleChange}
              className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Mobile Number */}
          <div className="space-y-2">
            <label htmlFor="mobileNumber" className="text-sm font-medium text-gray-700">
              Mobile Number
            </label>
            <input
              id="mobileNumber"
              name="mobileNumber"
              type="tel"
              placeholder="Enter 10-digit mobile number"
              value={formData.mobileNumber}
              onChange={handleChange}
              maxLength="10"
              className={`w-full p-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                errors.mobileNumber ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.mobileNumber && <p className="text-red-500 text-xs">{errors.mobileNumber}</p>}
          </div>

          {/* Address */}
          <div className="space-y-2">
            <label htmlFor="address" className="text-sm font-medium text-gray-700">
              Address
            </label>
            <input
              id="address"
              name="address"
              type="text"
              placeholder="Enter address"
              value={formData.address}
              onChange={handleChange}
              className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
        </div>

        {/* Notes */}
        <div className="space-y-2">
          <label htmlFor="notes" className="text-sm font-medium text-gray-700">
            Notes
          </label>
          <textarea
            id="notes"
            name="notes"
            placeholder="Enter any additional notes about the vehicle"
            value={formData.notes}
            onChange={handleChange}
            rows={4}
            className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 pt-4">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className={`flex-1 py-3 rounded-md transition-colors duration-200 font-medium ${
              loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            } text-white`}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Creating Vehicle...
              </div>
            ) : (
              'Create Vehicle'
            )}
          </button>
          
          <button
            type="button"
            onClick={handleReset}
            disabled={loading}
            className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reset Form
          </button>
        </div>

      {/* Form Info */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
        <div className="flex">
          <div className="text-blue-600">
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-2">
            <p className="text-sm text-blue-800">
              <span className="font-medium">Note:</span> Fields marked with * are required. Vehicle number will be automatically converted to uppercase and must be unique.
            </p>
          </div>
        </div>
      </div>
    </div>
    </div>
  )
}

export default FormCreateVehicle